import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Statistic, Space, Button, Switch, Table, Tag, Spin, message,
  Tooltip, Select, Badge, Input,
} from 'antd';
import {
  EnvironmentOutlined, HomeOutlined, ReloadOutlined,
  CarOutlined, ThunderboltOutlined, DisconnectOutlined, CheckCircleOutlined,
  FilterOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import Footer from '../components/Footer';

const { Option } = Select;

const PARAGUAY_CENTER = [-23.4425, -58.4438];
const PARAGUAY_BOUNDS = [[-27.5, -62.5], [-19.2, -54.5]];

const parseDt = (dt) => {
  if (!dt || dt === '0000-00-00 00:00:00') return null;
  return new Date(dt.includes('T') ? dt : dt.replace(' ', 'T'));
};

const estaOnline = (dt) => {
  const d = parseDt(dt);
  return d ? (Date.now() - d.getTime()) < 3600000 : false;
};

const markerHtml = (online, moving) => {
  const color  = !online ? '#9e9e9e' : moving ? '#4caf50' : '#f44336';
  const shadow = !online ? 'rgba(0,0,0,0.25)' : moving ? 'rgba(76,175,80,0.5)' : 'rgba(244,67,54,0.5)';
  const size   = online ? 18 : 14;
  return `<div style="
    width:${size}px; height:${size}px; border-radius:50%;
    background:${color}; border:2.5px solid white;
    box-shadow:0 0 0 3px ${shadow}, 0 2px 6px rgba(0,0,0,0.35);
    transition:all 0.3s;
  "></div>`;
};

const fmtDt = (dt) => {
  const d = parseDt(dt);
  if (!d || isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-PY', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const SERVICIO_LABELS = {
  RASTREO: 'Rastreo',
  VISION: 'HSS Vision',
  RASTREO_VISION: 'Rastreo + Vision',
};

const Mapa = () => {
  const navigate    = useNavigate();
  const mapDivRef   = useRef(null);
  const mapRef      = useRef(null);
  const markersRef  = useRef([]);
  const intervalRef = useRef(null);

  const [ubicaciones, setUbicaciones] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedPlate, setSelectedPlate] = useState(null);
  const primeraVez = useRef(true);

  // Filtros
  const [busqueda,       setBusqueda]       = useState('');
  const [filtroEstado,   setFiltroEstado]   = useState(null);
  const [filtroTipo,     setFiltroTipo]     = useState(null);
  const [filtroServicio, setFiltroServicio] = useState(null);
  const [pageSize, setPageSize] = useState(25);

  const cargar = useCallback(async () => {
    if (primeraVez.current) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const r = await axiosInstance.get('/tracking/ubicaciones');
      setUbicaciones(Array.isArray(r.data) ? r.data : []);
    } catch {
      message.error('No se pudieron obtener las posiciones GPS');
    } finally {
      setLoading(false);
      setRefreshing(false);
      primeraVez.current = false;
    }
  }, []);

  useEffect(() => {
    axiosInstance.post('/tracking/sincronizar-vehiculos').catch(() => {});
  }, []);

  // Init mapa Leaflet
  useEffect(() => {
    if (mapRef.current || !mapDivRef.current || !window.L) return;
    const L = window.L;
    const map = L.map(mapDivRef.current, { center: PARAGUAY_CENTER, zoom: 6 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    cargar();
  }, [cargar]);

  // Auto-refresh
  useEffect(() => {
    clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(cargar, 60000);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, cargar]);

  // Datos filtrados
  const ubicacionesFiltradas = useMemo(() => {
    return ubicaciones.filter(u => {
      const online = estaOnline(u.dt_tracker);
      const moving = online && parseFloat(u.speed || 0) > 0;
      if (filtroEstado === 'online'   && !online)           return false;
      if (filtroEstado === 'offline'  && online)            return false;
      if (filtroEstado === 'moving'   && !moving)           return false;
      if (filtroEstado === 'stopped'  && (moving || !online)) return false;
      if (filtroTipo     && u.db_tipo     !== filtroTipo)     return false;
      if (filtroServicio && u.db_servicio !== filtroServicio) return false;
      if (busqueda) {
        const q = busqueda.toLowerCase();
        const chapa    = (u.vehiculo_chapa || u.plate_number || u.name || '').toLowerCase();
        const alias    = (u.db_alias || '').toLowerCase();
        const nombreGps= (u.db_nombre_gps || u.name || '').toLowerCase();
        const cliente  = (u.cliente_nombre || '').toLowerCase();
        if (!chapa.includes(q) && !alias.includes(q) && !nombreGps.includes(q) && !cliente.includes(q)) return false;
      }
      return true;
    });
  }, [ubicaciones, busqueda, filtroEstado, filtroTipo, filtroServicio]);

  // Opciones dinámicas para los selects
  const tiposDisponibles = useMemo(() =>
    [...new Set(ubicaciones.map(u => u.db_tipo).filter(Boolean))].sort(),
    [ubicaciones]);

  const serviciosDisponibles = useMemo(() =>
    [...new Set(ubicaciones.map(u => u.db_servicio).filter(Boolean))].sort(),
    [ubicaciones]);

  const hayFiltros = busqueda || filtroEstado || filtroTipo || filtroServicio;

  // Actualizar marcadores (solo los filtrados)
  useEffect(() => {
    const L = window.L;
    if (!L || !mapRef.current) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    ubicacionesFiltradas.forEach(u => {
      const lat = parseFloat(u.lat);
      const lng = parseFloat(u.lng);
      if (!lat && !lng) return;
      if (u.loc_valid === '0' || u.loc_valid === 0) return;

      const online  = estaOnline(u.dt_tracker);
      const spd     = parseFloat(u.speed || 0);
      const moving  = spd > 0;
      const chapa   = u.vehiculo_chapa || u.plate_number || u.name;
      const cliente = u.cliente_nombre || '';
      const modelo  = u.db_modelo || u.model || '';
      const acc     = u.params?.acc === '1' || u.params?.acc === 1
                  || u.params?.io239 === '1' || u.params?.io239 === 1;

      const sz   = online ? 18 : 14;
      const icon = L.divIcon({
        html: markerHtml(online, moving),
        className: '',
        iconSize: [sz, sz],
        iconAnchor: [sz / 2, sz / 2],
        popupAnchor: [0, -(sz / 2 + 4)],
      });

      const popup = `
        <div style="min-width:180px;font-family:sans-serif;line-height:1.6">
          <div style="font-weight:700;font-size:15px;font-family:monospace;color:#3b5998">${chapa}</div>
          ${modelo ? `<div style="font-size:12px;color:#888">${modelo}</div>` : ''}
          ${cliente ? `<div style="font-size:12px;color:#555">${cliente}</div>` : ''}
          <div style="margin-top:6px;font-size:12px">
            <span style="color:${!online ? '#9e9e9e' : moving ? '#4caf50' : '#f44336'};font-weight:600">${!online ? '○ Sin señal' : moving ? '● En movimiento' : '● Detenido'}</span>
            ${acc ? ' · <span style="color:#faad14">🔑 Encendido</span>' : ''}
            ${moving ? ` · <b>${Math.round(spd)} km/h</b>` : ''}
          </div>
          <div style="font-size:11px;color:#aaa;margin-top:2px">${fmtDt(u.dt_tracker)}</div>
          ${u.vehiculo_chapa ? `<div style="margin-top:6px"><a href="/vehiculos/${u.vehiculo_chapa}" target="_blank" style="color:#3b5998;font-size:12px;font-weight:600">Ver vehículo →</a></div>` : ''}
        </div>
      `;

      const marker = L.marker([lat, lng], { icon })
        .addTo(mapRef.current)
        .bindPopup(popup);

      marker.on('click', () => setSelectedPlate(u.plate_number));
      markersRef.current.push(marker);
    });
  }, [ubicacionesFiltradas]);

  const centrar = () => mapRef.current?.fitBounds(PARAGUAY_BOUNDS);

  const volarA = (lat, lng, plate) => {
    if (mapRef.current && lat && lng) {
      mapRef.current.setView([parseFloat(lat), parseFloat(lng)], 15);
      setSelectedPlate(plate);
    }
  };

  // Stats del total sin filtro
  const total     = ubicaciones.length;
  const online    = ubicaciones.filter(u => estaOnline(u.dt_tracker)).length;
  const moving    = ubicaciones.filter(u => estaOnline(u.dt_tracker) && parseFloat(u.speed || 0) > 0).length;
  const sinSenial = ubicaciones.filter(u => !estaOnline(u.dt_tracker)).length;
  const conFix    = ubicaciones.filter(u => parseFloat(u.lat) || parseFloat(u.lng)).length;

  // Stats filtradas
  const filtTotal     = ubicacionesFiltradas.length;
  const filtOnline    = ubicacionesFiltradas.filter(u => estaOnline(u.dt_tracker)).length;
  const filtMoving    = ubicacionesFiltradas.filter(u => estaOnline(u.dt_tracker) && parseFloat(u.speed || 0) > 0).length;
  const filtSinSenial = ubicacionesFiltradas.filter(u => !estaOnline(u.dt_tracker)).length;

  const columns = [
    {
      title: 'Estado', key: 'estado', width: 100,
      sorter: (a, b) => {
        const ao = estaOnline(a.dt_tracker) ? (parseFloat(a.speed || 0) > 0 ? 2 : 1) : 0;
        const bo = estaOnline(b.dt_tracker) ? (parseFloat(b.speed || 0) > 0 ? 2 : 1) : 0;
        return ao - bo;
      },
      render: (_, r) => {
        const on  = estaOnline(r.dt_tracker);
        const mov = on && parseFloat(r.speed || 0) > 0;
        if (!on)  return <Tag color="default">Sin señal</Tag>;
        if (mov)  return <Tag color="success"><ThunderboltOutlined /> En movimiento</Tag>;
        return <Tag color="orange">Detenido</Tag>;
      },
    },
    {
      title: 'Chapa / Nombre', key: 'chapa',
      sorter: (a, b) => {
        const ca = a.vehiculo_chapa || a.plate_number || a.name || '';
        const cb = b.vehiculo_chapa || b.plate_number || b.name || '';
        return ca.localeCompare(cb);
      },
      render: (_, r) => {
        const chapa  = r.vehiculo_chapa || r.plate_number || r.name;
        const modelo = r.db_modelo || r.model || '';
        const tipo   = r.db_tipo;
        return (
          <Space direction="vertical" size={0}>
            <Space size={4}>
              <span
                style={{ fontWeight: 700, fontFamily: 'monospace', color: hssColors.primary, cursor: r.vehiculo_chapa ? 'pointer' : 'default' }}
                onClick={() => r.vehiculo_chapa && navigate(`/vehiculos/${r.vehiculo_chapa}`)}
              >
                {chapa}
              </span>
              {tipo && <Tag style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>{tipo}</Tag>}
            </Space>
            {modelo && <span style={{ fontSize: 12, color: hssColors.textSecondary }}>{modelo}</span>}
          </Space>
        );
      },
    },
    {
      title: 'Cliente', dataIndex: 'cliente_nombre', key: 'cliente',
      sorter: (a, b) => (a.cliente_nombre || '').localeCompare(b.cliente_nombre || ''),
      render: n => n || <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Servicio', key: 'servicio', width: 130,
      sorter: (a, b) => (a.db_servicio || '').localeCompare(b.db_servicio || ''),
      render: (_, r) => {
        if (!r.db_servicio) return <span style={{ color: hssColors.gray400 }}>—</span>;
        const colors = { RASTREO: 'blue', VISION: 'red', RASTREO_VISION: 'purple' };
        return <Tag color={colors[r.db_servicio] || 'default'} style={{ fontSize: 11 }}>{SERVICIO_LABELS[r.db_servicio] || r.db_servicio}</Tag>;
      },
    },
    {
      title: 'Ignición', key: 'acc', width: 90,
      sorter: (a, b) => {
        const aOn = a.params?.acc === '1' || a.params?.acc === 1 || a.params?.io239 === '1' || a.params?.io239 === 1;
        const bOn = b.params?.acc === '1' || b.params?.acc === 1 || b.params?.io239 === '1' || b.params?.io239 === 1;
        return (aOn ? 1 : 0) - (bOn ? 1 : 0);
      },
      render: (_, r) => {
        const on = r.params?.acc === '1' || r.params?.acc === 1
                || r.params?.io239 === '1' || r.params?.io239 === 1;
        return <Tag color={on ? 'success' : 'default'}>{on ? 'ON' : 'OFF'}</Tag>;
      },
    },
    {
      title: 'Velocidad', key: 'speed', width: 110,
      sorter: (a, b) => parseFloat(a.speed || 0) - parseFloat(b.speed || 0),
      render: (_, r) => {
        const spd = parseFloat(r.speed || 0);
        return spd > 0
          ? <Tag color="orange"><ThunderboltOutlined /> {Math.round(spd)} km/h</Tag>
          : <span style={{ color: hssColors.gray400 }}>Detenido</span>;
      },
    },
    {
      title: 'Voltaje', key: 'volt', width: 90,
      sorter: (a, b) => {
        const av = parseFloat(a.params?.acm0 || a.params?.io66 || a.params?.pwr || 0);
        const bv = parseFloat(b.params?.acm0 || b.params?.io66 || b.params?.pwr || 0);
        return av - bv;
      },
      render: (_, r) => {
        const raw = parseFloat(r.params?.acm0 || r.params?.io66 || r.params?.pwr || 0);
        if (!raw) return <span style={{ color: hssColors.gray400 }}>—</span>;
        const volt = raw > 100 ? raw / 1000 : raw;
        return <span style={{ fontFamily: 'monospace' }}>{volt.toFixed(1)} V</span>;
      },
    },
    {
      title: 'Coordenadas', key: 'coords', width: 180,
      sorter: (a, b) => parseFloat(a.lat || 0) - parseFloat(b.lat || 0),
      render: (_, r) => {
        const lat = parseFloat(r.lat);
        const lng = parseFloat(r.lng);
        if (!lat && !lng) return <span style={{ color: hssColors.gray400 }}>Sin fix GPS</span>;
        return <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{lat.toFixed(5)}, {lng.toFixed(5)}</span>;
      },
    },
    {
      title: 'Última señal', key: 'dt', width: 150,
      render: (_, r) => <span style={{ fontSize: 12 }}>{fmtDt(r.dt_tracker)}</span>,
      sorter: (a, b) => new Date(a.dt_tracker || 0) - new Date(b.dt_tracker || 0),
      defaultSortOrder: 'descend',
    },
    {
      title: '', key: 'ir', width: 50,
      render: (_, r) => {
        const lat = parseFloat(r.lat);
        const lng = parseFloat(r.lng);
        if (!lat && !lng) return null;
        return (
          <Tooltip title="Centrar en mapa">
            <Button size="small" icon={<EnvironmentOutlined />} onClick={() => volarA(r.lat, r.lng, r.plate_number)} />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <EnvironmentOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Mapa GPS</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>
                Posición actual · {conFix} con señal de {total} vehículos
                {hayFiltros && <span style={{ color: hssColors.warning, fontWeight: 600 }}> · mostrando {filtTotal}</span>}
              </p>
            </div>
          </div>
          <Space>
            <Space size={4}>
              <Switch checked={autoRefresh} onChange={setAutoRefresh} size="small" />
              <span style={{ fontSize: 12, color: hssColors.textSecondary }}>Auto 60s</span>
            </Space>
            <Button icon={<ReloadOutlined />} onClick={cargar} loading={refreshing}>Actualizar</Button>
            <Button onClick={centrar}>Paraguay</Button>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '16px 32px', flex: 1 }}>

        {/* Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {[
            { title: 'Total vehículos', total, filtrado: filtTotal,    color: hssColors.primary,       icon: <CarOutlined /> },
            { title: 'Online (< 1h)',   total: online,   filtrado: filtOnline,   color: hssColors.success,       icon: <CheckCircleOutlined /> },
            { title: 'En movimiento',   total: moving,   filtrado: filtMoving,   color: hssColors.warning,       icon: <ThunderboltOutlined /> },
            { title: 'Sin señal',       total: sinSenial, filtrado: filtSinSenial, color: hssColors.textSecondary, icon: <DisconnectOutlined /> },
          ].map(s => (
            <Col xs={12} lg={6} key={s.title}>
              <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: `4px solid ${s.color}` }}>
                <Statistic
                  title={<span style={{ color: hssColors.textSecondary, fontSize: 13 }}>{s.title}</span>}
                  value={hayFiltros ? s.filtrado : s.total}
                  suffix={hayFiltros ? <span style={{ fontSize: 13, color: hssColors.textSecondary }}>/ {s.total}</span> : undefined}
                  prefix={s.icon}
                  valueStyle={{ color: s.color, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Mapa */}
        <Card
          style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, overflow: 'hidden', marginBottom: 16 }}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ position: 'relative' }}>
            <div ref={mapDivRef} style={{ height: 500, width: '100%' }} />
            {loading && (
              <div style={{
                position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.75)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500,
              }}>
                <Spin size="large" tip="Cargando posiciones..." />
              </div>
            )}
          </div>
          {/* Leyenda */}
          <div style={{ padding: '8px 16px', borderTop: `1px solid ${hssColors.gray200}`, display: 'flex', gap: 20, fontSize: 12, color: hssColors.textSecondary }}>
            <Space size={4}><span style={{ display:'inline-block', width:12, height:12, borderRadius:'50%', background:'#4caf50', boxShadow:'0 0 0 2px rgba(76,175,80,0.4)' }} /> En movimiento</Space>
            <Space size={4}><span style={{ display:'inline-block', width:12, height:12, borderRadius:'50%', background:'#f44336', boxShadow:'0 0 0 2px rgba(244,67,54,0.4)' }} /> Detenido (con señal)</Space>
            <Space size={4}><span style={{ display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#9e9e9e' }} /> Sin señal (&gt; 1h)</Space>
          </div>
        </Card>

        {/* Tabla */}
        <Card
          title={
            <Space>
              <CarOutlined />
              <span>Detalle de vehículos</span>
              {hayFiltros && (
                <Badge count={filtTotal} style={{ backgroundColor: hssColors.primary }} />
              )}
            </Space>
          }
          extra={
            <Space wrap>
              <FilterOutlined style={{ color: hssColors.textSecondary }} />
              <Input.Search
                placeholder="Buscar chapa, alias, cliente..."
                allowClear
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                style={{ width: 220 }}
                size="small"
              />
              <Select
                placeholder="Estado"
                allowClear
                value={filtroEstado}
                onChange={setFiltroEstado}
                style={{ width: 150 }}
                size="small"
              >
                <Option value="online">
                  <Tag color="success" style={{ margin: 0 }}>Online</Tag>
                </Option>
                <Option value="moving">
                  <Tag color="orange" style={{ margin: 0 }}>En movimiento</Tag>
                </Option>
                <Option value="stopped">
                  <Tag color="warning" style={{ margin: 0 }}>Detenido</Tag>
                </Option>
                <Option value="offline">
                  <Tag color="default" style={{ margin: 0 }}>Sin señal</Tag>
                </Option>
              </Select>

              {tiposDisponibles.length > 0 && (
                <Select
                  placeholder="Tipo vehículo"
                  allowClear
                  value={filtroTipo}
                  onChange={setFiltroTipo}
                  style={{ width: 140 }}
                  size="small"
                >
                  {tiposDisponibles.map(t => (
                    <Option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</Option>
                  ))}
                </Select>
              )}

              {serviciosDisponibles.length > 0 && (
                <Select
                  placeholder="Servicio"
                  allowClear
                  value={filtroServicio}
                  onChange={setFiltroServicio}
                  style={{ width: 160 }}
                  size="small"
                >
                  {serviciosDisponibles.map(s => (
                    <Option key={s} value={s}>{SERVICIO_LABELS[s] || s}</Option>
                  ))}
                </Select>
              )}

              {hayFiltros && (
                <Tooltip title="Limpiar filtros">
                  <Button
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => { setBusqueda(''); setFiltroEstado(null); setFiltroTipo(null); setFiltroServicio(null); }}
                  >
                    Limpiar
                  </Button>
                </Tooltip>
              )}
            </Space>
          }
          style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}
          styles={{ body: { padding: 0 } }}
        >
          <Table
            columns={columns}
            dataSource={ubicacionesFiltradas}
            rowKey={r => r.plate_number || r.imei || r.name}
            size="small"
            loading={loading || refreshing}
            scroll={{ x: 'max-content' }}
            rowClassName={r => r.plate_number === selectedPlate ? 'ant-table-row-selected' : ''}
            pagination={{
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              onShowSizeChange: (_, size) => setPageSize(size),
              showTotal: (t, range) => `${range[0]}-${range[1]} de ${t} vehículos`,
            }}
            locale={{ emptyText: loading ? 'Cargando...' : 'Sin datos del servidor GPS' }}
          />
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default Mapa;
